import Editor from "@monaco-editor/react";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import pluralize from "pluralize";
import {
  Box,
  Button,
  ButtonGroup,
  DarkMode,
  Flex,
  Stack,
  Text,
  useTheme,
} from "@chakra-ui/react";
import {
  CODE_CHALLENGE_LOADING_HEIGHT,
  CodeChallengeLoading,
} from "./CodeChallengeLoading";
import {
  FiAlertCircle,
  FiCheck,
  FiCheckCircle,
  FiPlay,
  FiRotateCcw,
} from "react-icons/fi";
import { MAX_TRIALS, TaskContent, useTask, useTrials } from "../Task";
import {
  STUDIO_THEME_EDITABLE_DARK,
  studioThemeEditableDark,
} from "@utils/monaco";
import { inRange, sortBy } from "lodash";
import { outdent } from "outdent";
import { useConfetti } from "@utils";
import type { Range, editor } from "monaco-editor";

export type Result = {
  pass: boolean;
  description: string;
};

export type EditorData = {
  initialValue: string;
  solution: string;
};

type CodeChallengeEditorProps = {
  data: EditorData;
  taskId: string;
  functionUrl: string;
  complete: boolean;
  setComplete: Dispatch<SetStateAction<boolean>>;
};

export function CodeChallengeEditor({
  data,
  taskId,
  functionUrl,
  complete,
  setComplete,
}: CodeChallengeEditorProps) {
  const editorRef = useRef<editor.IStandaloneCodeEditor>();
  const rangeRef =
    useRef<(...args: ConstructorParameters<typeof Range>) => Range>();
  const decorationsRef = useRef<string[]>([]);

  const [height, setHeight] = useState(CODE_CHALLENGE_LOADING_HEIGHT);
  const { trialsRemaining, resetTrials } = useTrials();
  const [results, setResults] = useState<Result[]>([]);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);

  const [solutionShown, setSolutionShown] = useState(false);
  const { task, saveTask, savingTask } = useTask(taskId);
  const [currentValue, setCurrentValue] = useState(task?.value ?? null);

  const { fonts } = useTheme();
  const [runButtonRef, confetti] = useConfetti<HTMLButtonElement>();

  const regex = useMemo(() => {
    if (!/(>>>|<<<)/.test(data.initialValue)) {
      return null;
    }

    const catchall = "(?:.|\n)*?";
    const pattern = data.initialValue
      .replace(/[.*+?^${}()/|[\]\\]/g, "\\$&") // escape special chars

      // replace all areas flagged as editable with the `catchall` string
      .replace(new RegExp(` *>>>${catchall}<<<`, "g"), catchall)

      // get editable area at end of code block (doesn't need closing <<<)
      .replace(new RegExp(` *>>>${catchall}$`, "g"), catchall)

      // get editable area at beginning of code block (doesn't need opening >>>)
      .replace(new RegExp(`^${catchall}<<<`, "g"), catchall)

      // create an array of the non-catchall parts (segments)
      .split(catchall)

      // wrap each segment in a capture group
      .map((segment) => `(${segment})`)

      // put catchalls back between each segment
      .join(catchall)

      // escape newlines
      .replace(/\n/g, "\\n")

      // remove empty capture groups at the beginning or end of the pattern
      .replace(/^\(\)|\(\)$/, "");

    return new RegExp(`^${pattern}$`);
  }, [data.initialValue]);

  const initialValue = useMemo(
    () =>
      data.initialValue
        .split("\n")
        .filter((line) => {
          const value = line.trim();
          return value !== ">>>" && value !== "<<<";
        })
        .join("\n"),
    [data.initialValue]
  );

  const reset = useCallback(() => {
    editorRef.current?.setValue(initialValue);

    resetTrials();
    setCurrentValue(null);
    setSolutionShown(false);
    setResults([]);
    setComplete(false);
    saveTask(null, null);
  }, [initialValue, saveTask, setComplete, resetTrials]);

  const run = useCallback(async () => {
    setLoading(true);

    const userCode = editorRef.current?.getValue();
    const response = await fetch(functionUrl, {
      method: "POST",
      body: userCode,
    });

    const results: Result[] = await response.json();

    setLoading(false);
    setResults(results);

    // if every test passes, the challenge is complete
    const isComplete = results.every((result) => result.pass);

    // if complete we don't count trials, so the solutions button doesn't show
    resetTrials(isComplete);
    setComplete(isComplete);
    saveTask(isComplete ? new Date() : null, userCode);

    if (isComplete) {
      confetti();
    }

    window.gtag?.(
      "event",
      isComplete
        ? solutionShown
          ? "editor_pass_w_solution"
          : "editor_pass"
        : "editor_fail",
      {
        task_id: taskId,
        response: userCode,
      }
    );
  }, [
    confetti,
    functionUrl,
    saveTask,
    setComplete,
    solutionShown,
    taskId,
    resetTrials,
  ]);

  const showSolution = useCallback(() => {
    const userCode = editorRef.current?.getValue();
    editorRef.current?.setValue(outdent`
      // Here's the solution (your code is commented out after):
      ${data.solution}
      /* your code:
      ${userCode}
      */
    `);

    setSolutionShown(true);
    run();
  }, [data.solution, run]);

  // returns an array of arrays
  // each inner array contains the starting and ending line number of areas that are read-only
  const getReadOnlyAreas = useCallback(
    (value: string): [number, number][] => {
      let offset = 0; // character index offset
      const matches = value.match(regex || "");
      return matches
        ? matches.slice(1).map((match) => {
            // TODO: account for editable area with same value as read-only one
            const index = value.indexOf(match, offset);
            offset = index + match.length;
            const start = index && value.slice(0, index).split("\n").length;

            // match.trim() chops off too much white space
            // only want to take off 1 carriage return at beginning and end of match
            // if there is more than 1, leave the rest to preserve intentional blank lines
            const endOffset = match.split("\n");

            if (endOffset[endOffset.length - 1] === "") {
              endOffset.pop();
            }

            if (endOffset[0] === "") {
              endOffset.shift();
            }

            return [start + 1, start + endOffset.length];
          })
        : [];
    },
    [regex]
  );

  const applyDecorations = useCallback(
    (value: string) => {
      const createRange = rangeRef.current;
      if (!createRange) {
        return;
      }

      const decorations = getReadOnlyAreas(value).map(([start, end]) => {
        const range = createRange(start, 1, end, 1);
        return {
          range,
          options: {
            isWholeLine: true,
            className: "readonly",
          },
        };
      });

      // keep track of decorations
      if (editorRef.current) {
        decorationsRef.current = editorRef.current.deltaDecorations(
          decorationsRef.current, // to invalidate old ones
          decorations // when we add new ones
        );
      }
    },
    [getReadOnlyAreas]
  );

  return (
    <div>
      <DarkMode>
        <Box
          data-cy={`monaco-${taskId}`}
          style={{ height }}
          pos="relative"
          className={focused ? "focused" : undefined}
          sx={{
            ".monaco-editor": {
              boxSize: "full",
              roundedTop: "md",
              bg: "gray.900",
              pos: "absolute",
              top: 0,
              left: 0,
              [[".margin", ".monaco-editor-background"].toString()]: {
                bgColor: "transparent",
              },
              ".readonly": {
                pointerEvents: "none",
                bgColor: "gray.900",
                opacity: 1 / 2,
                zIndex: 1,
                transition: "opacity 250ms",
              },
              ":not(.focused) .readonly": {
                opacity: 0,
              },
            },
          }}
        >
          <Editor
            language="javascript"
            theme={STUDIO_THEME_EDITABLE_DARK}
            value={currentValue ?? initialValue}
            loading={
              <CodeChallengeLoading roundedBottom="none" w="full">
                Loading editor
              </CodeChallengeLoading>
            }
            options={{
              tabSize: 2,
              fontSize: 16,
              fontFamily: fonts.mono,
              lineNumbersMinChars: 3,
              automaticLayout: true,
              minimap: {
                enabled: false,
              },
              scrollbar: {
                vertical: "hidden",
                handleMouseWheel: false,
              },
              overviewRulerBorder: false,
              scrollBeyondLastLine: false,
              wordWrap: "on",
              padding: {
                top: 12,
                bottom: 12,
              },
            }}
            beforeMount={(monaco) => {
              monaco.editor.defineTheme(
                STUDIO_THEME_EDITABLE_DARK,
                studioThemeEditableDark
              );

              rangeRef.current = (
                ...args: ConstructorParameters<typeof monaco.Range>
              ) => new monaco.Range(...args);
            }}
            onMount={(editor, monaco) => {
              // enable auto-expansion
              // TEMP-FIX: short timeout solves the super tall editor issue.
              setTimeout(() => {
                // TODO: use a ref and set the height directly
                setHeight(editor.getContentHeight());
              }, 100);

              editor.onDidChangeModelContent(() => {
                setHeight(editor.getContentHeight());
                applyDecorations(editor.getValue());
              });

              editor.onDidFocusEditorText(() => setFocused(true));
              editor.onDidBlurEditorText(() => setFocused(false));

              editor.onDidChangeCursorPosition(() => {
                const textModel = editor.getModel();

                if (textModel) {
                  // get all of the current lines in the editor
                  const lines = textModel.getLinesContent();

                  const editableLines: number[] = [];
                  const readOnlyAreas = getReadOnlyAreas(editor.getValue());

                  // determine which lines are editable ones
                  lines.forEach((line, index) => {
                    const lineNumber = index + 1;
                    const isEditable = !readOnlyAreas.some(([start, end]) =>
                      inRange(lineNumber, start, end + 1)
                    );
                    if (isEditable) {
                      editableLines.push(lineNumber);
                    }
                  });

                  const position = editor.getPosition();
                  if (position) {
                    // if the new line number is not editable
                    if (!editableLines.includes(position.lineNumber)) {
                      // find nearest editable line
                      const [nextEditableLine] = sortBy(editableLines, (line) =>
                        Math.abs(position.lineNumber - line)
                      );
                      // calculate the length of the line
                      const column = lines[nextEditableLine - 1].length + 1;
                      // put the cursor at the end of the nearest editable line
                      editorRef.current?.setPosition({
                        column,
                        lineNumber: nextEditableLine,
                      });
                    }
                  }
                }
              });

              // --- Key Detection ---

              // Context keys are global (cross editor), so we need a random key per
              // editor or else the focus value will be true if _any_ editor is focused
              // instead of just the one this component handles
              const focusKey = (Math.random() * 100000).toFixed(0).toString();
              const focused = editor.createContextKey<boolean>(focusKey, false);

              // Enable context when focused and disable when blurred
              editor.onDidFocusEditorText(() => focused.set(true));
              editor.onDidBlurEditorText(() => focused.set(false));

              // When CTRL or CMD + Enter is pressed, submit the document
              editor.addCommand(
                monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter,
                () => {
                  run();
                },
                focusKey
              );

              // Spare the accidental and instinctual CMD + S's
              editor.addCommand(
                monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
                () => undefined,
                focusKey
              );

              // set ref for later use, like in applyDecorations below
              editorRef.current = editor;

              // apply line decorations if necessary
              applyDecorations(editor.getValue());
            }}
            onChange={(value, event) => {
              if (
                !solutionShown &&
                !event.isUndoing &&
                regex &&
                typeof value !== "undefined" &&
                !regex.test(value)
              ) {
                // undo the last input if it changes a non-editable area
                editorRef.current?.trigger(null, "undo", null);
              }
            }}
          />
        </Box>
        <Flex
          justify="space-between"
          p="2"
          borderTopWidth="1px"
          borderTopColor="gray.700"
          color="white"
          bg="gray.900"
          roundedBottom="md"
        >
          <Box pos="relative">
            <Button
              fontSize="md"
              size="sm"
              isLoading={loading || savingTask}
              onClick={run}
              colorScheme={complete ? "green" : "gray"}
              rightIcon={complete ? <FiCheck /> : <FiPlay />}
              data-cy={`submit-code-${taskId}`}
              ref={runButtonRef}
            >
              {complete ? "Completed" : "Run"}
            </Button>
          </Box>
          <ButtonGroup size="sm" variant="ghost">
            {trialsRemaining > 0 && trialsRemaining < MAX_TRIALS && (
              <Text alignSelf="center" fontSize="md">
                Solution available in{" "}
                {pluralize("attempt", trialsRemaining, true)}
              </Text>
            )}
            {trialsRemaining <= 0 && !solutionShown && (
              <Button
                fontSize="md"
                colorScheme="green"
                isDisabled={savingTask}
                onClick={() => showSolution()}
                data-cy={`solution-${taskId}`}
              >
                Show solution
              </Button>
            )}
            {(results.length > 0 || complete || currentValue) && (
              <Button
                fontSize="md"
                isDisabled={savingTask}
                onClick={reset}
                colorScheme="red"
                rightIcon={<FiRotateCcw />}
                data-cy={`reset-${taskId}`}
              >
                Reset
              </Button>
            )}
          </ButtonGroup>
        </Flex>
      </DarkMode>
      {results.length > 0 && (
        <Stack mt="4" spacing="1" data-cy="results">
          {results.map((result, index) => (
            <Flex
              key={index}
              align="center"
              data-cy="test-response"
              data-pass={result.pass}
              fontSize="md"
              color={result.pass ? "success" : "failure"}
            >
              <Box mr="2">
                {result.pass ? <FiCheckCircle /> : <FiAlertCircle />}
              </Box>
              <span>
                <TaskContent>{result.description}</TaskContent>
              </span>
            </Flex>
          ))}
        </Stack>
      )}
    </div>
  );
}
